import { CCard, CCardBody, CCardHeader, CCol } from '@coreui/react';
import DataTable from 'components/common/DataTable';
import Button from 'components/common/Button';
import Loading from 'components/common/Loading';
import useTotalCO2SavingsReport from 'hooks/useTotalCO2SavingsReport';
import { pick } from 'lodash';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { routePatterns } from 'router/route-paths';
import { value2LocaleValue } from 'utils/helpers';
import type { IntlShape } from 'react-intl';
import type { TCO2SavingsCategory, TTotalCO2SavingsReport } from 'hooks/useTotalCO2SavingsReport';
import s from './CO2SavingsReport.module.scss';

const co2SavingsKeys = [
  'new_windows',
  'insulation_top_ceiling',
  'insulation_basement_ceiling',
  'insulation_facade',
  'controlled_living_space_ventilation',
  'heating_system',
  'solar_power_system',
];

/** The [category, savings] entries of the report's measures (pick leaves out the missing ones). */
const co2SavingsEntriesOf = (
  report: TTotalCO2SavingsReport | null,
): [string, TCO2SavingsCategory][] => {
  const entries: [string, TCO2SavingsCategory][] = [];
  Object.entries(pick(report, co2SavingsKeys)).forEach(([key, category]) => {
    if (category !== undefined) entries.push([key, category]);
  });
  return entries;
};

interface ITotalCO2SavingsTableProps {
  totalCO2SavingsReport: TTotalCO2SavingsReport | null;
  t: IntlShape['formatMessage'];
}

const TotalCO2SavingsTable = ({ totalCO2SavingsReport, t }: ITotalCO2SavingsTableProps) => {
  const tableHead = useMemo(
    () => [
      { key: 'relevantCategory', label: t({ id: 'co2_report.category_name' }) },
      {
        key: 'countOfProjectsWithStatusPlanned',
        label: t({ id: 'co2_report.number_of_status_planned' }),
      },
      { key: 'plannedCO2Savings', label: t({ id: 'co2_report.co2_savings' }) },
      {
        key: 'countOfProjectsWithStatusProgress',
        label: t({ id: 'co2_report.number_of_status_progress' }),
      },
      { key: 'progressCO2Savings', label: t({ id: 'co2_report.co2_savings' }) },
      {
        key: 'countOfProjectsWithStatusDone',
        label: t({ id: 'co2_report.number_of_status_done' }),
      },
      { key: 'doneCO2Savings', label: t({ id: 'co2_report.co2_savings' }) },
    ],
    [t],
  );

  const co2SavingsItems = useMemo(
    () => co2SavingsEntriesOf(totalCO2SavingsReport),
    [totalCO2SavingsReport],
  );
  const co2SavingsTotal = useMemo(
    () =>
      co2SavingsItems.reduce(
        (sum, [, { co2_savings }]) => sum + Math.round(Number(co2_savings) / 1000),
        0,
      ),
    [co2SavingsItems],
  );

  const items = useMemo(() => {
    const tableRows = co2SavingsItems.map(([categoryKey, co2SavingsCategory]) => {
      const { co2_savings, projects_count } = co2SavingsCategory;
      return {
        relevantCategory: t({ id: `co2_report.${categoryKey}` }),
        countOfProjectsWithStatusPlanned: value2LocaleValue(projects_count),
        // Number(null) is 0, as null / 1000 was.
        plannedCO2Savings: value2LocaleValue(Math.round(Number(co2_savings) / 1000)),
        countOfProjectsWithStatusProgress: t({ id: 'co2_report.coming_soon' }),
        progressCO2Savings: t({ id: 'co2_report.coming_soon' }),
        countOfProjectsWithStatusDone: t({ id: 'co2_report.coming_soon' }),
        doneCO2Savings: t({ id: 'co2_report.coming_soon' }),
      };
    });
    const tableFooterRow = {
      relevantCategory: ' ',
      countOfProjectsWithStatusPlanned: ' ',
      plannedCO2Savings: value2LocaleValue(co2SavingsTotal),
      countOfProjectsWithStatusProgress: ' ',
      progressCO2Savings: ' ',
      countOfProjectsWithStatusDone: ' ',
      doneCO2Savings: ' ',
    };
    tableRows.push(tableFooterRow);
    return tableRows;
  }, [co2SavingsItems, co2SavingsTotal, t]);

  return (
    <DataTable
      addTableClasses={s.co2ReportTable}
      hover
      items={items}
      fields={tableHead}
      size="sm"
    />
  );
};

const CO2SavingsReport = () => {
  const { formatMessage: t } = useIntl();
  const [totalCO2SavingsReport, loading] = useTotalCO2SavingsReport();

  return (
    <CCol xs={12} lg={12}>
      <CCard>
        <CCardHeader
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <h4>{t({ id: 'page_titles.co2_report' })}</h4>
          <Link to={routePatterns.co2SavingsByProjects.stringify({ page: 1 })}>
            <Button>{t({ id: 'co2_report.go_to_details' })}</Button>
          </Link>
        </CCardHeader>
        <CCardBody>
          {!loading ? (
            <TotalCO2SavingsTable {...{ totalCO2SavingsReport, t }} />
          ) : (
            <Loading size={30} />
          )}
        </CCardBody>
      </CCard>
    </CCol>
  );
};

export default CO2SavingsReport;
